import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, forkJoin, map, Observable, of } from 'rxjs';
import { MandateAssessmentStatus } from '../modules/caseload/interfaces/mandate-assessment-status';
import { CaseloadService } from '../modules/caseload/services/caseload.service';
import { ReferenceService } from '../services/reference.service';
import { Constants } from '../shared/constants/constants';
import { IReferenceData } from '../shared/interfaces/form/reference-data.model';

@Injectable({
  providedIn: 'root',
})
export class ReferenceStore {
  private cache: Map<string, BehaviorSubject<IReferenceData[] | null>> =
    new Map<string, BehaviorSubject<IReferenceData[] | null>>();

  constructor(private referenceService: ReferenceService, private caseloadService: CaseloadService) { }

  private load(ddName: string): Observable<IReferenceData[]> {
    if (!this.cache.has(ddName)) {
      const subject: BehaviorSubject<IReferenceData[] | null> =
        new BehaviorSubject<IReferenceData[] | null>(null);
      this.cache.set(ddName, subject);
      if (ddName === 'lcgmsLocations')
        this.referenceService.getLcgmsLocations().subscribe({
          next: (data: IReferenceData[]) => subject.next(data),
          error: (err: HttpErrorResponse) => subject.error(err),
        });
      else if (ddName === 'Mandates' || ddName === 'Assessments') {
        const endpointMap: Record<string, string> = {
          Mandates: 'ProviderManagement/GetMandateStatus',
          Assessments: 'EvaluatorAssignment/GetEvalAssessmentStatus',
          Encounters: '',
        };
        const endpoint: string = endpointMap[ddName];
        this.caseloadService.fetchMandatesAssessments(endpoint).pipe(
          map((data: MandateAssessmentStatus[]) =>
            data?.filter(d => d.statusId)?.map(d => ({
              id: d.statusId,
              description: d.statusDescription
            }))
          )
        ).subscribe({
          next: (mappedData) => subject.next(mappedData),
          error: (err: HttpErrorResponse) => subject.error(err),
        });
      }
      else
        this.referenceService?.getDropDownValues(ddName).subscribe({
          next: (data: IReferenceData[]) => subject.next(data),
          error: (err: HttpErrorResponse) => subject.error(err),
        });
    }

    return this.cache
      .get(ddName)!
      .asObservable()
      .pipe(filter((v: IReferenceData[]) => v !== null));
  }

  get otherImpairment$(): Observable<IReferenceData[]> {
    return this.load('OtherImpairment');
  }

  get disabilityClass$(): Observable<IReferenceData[]> {
    return this.load('DisabilityClass');
  }

  get state$(): Observable<IReferenceData[]> {
    return this.load('State');
  }

  get language$(): Observable<IReferenceData[]> {
    return this.load('Language').pipe(
      map((languages: IReferenceData[]) =>
        languages.filter(language => language.id !== 227)
      )
    );
  }

  get languageSpeech$(): Observable<IReferenceData[]> {
    return this.load('Language');
  }
  get rateRange$(): Observable<IReferenceData[]> {
    return of(Constants.RATE_RANGE);
  }
  get issuingReason$(): Observable<IReferenceData[]> {
    return this.load('IssuingReason');
  }

  get relationship$(): Observable<IReferenceData[]> {
    return this.load('Relationship');
  }

  get personCategory$(): Observable<IReferenceData[]> {
    return this.load('PersonCategory');
  }

  get pwnSubject$(): Observable<IReferenceData[]> {
    return this.load('PWNSubject');
  }

  get waitTime$(): Observable<IReferenceData[]> {
    return this.load('WaitTime');
  }

  get district$(): Observable<IReferenceData[]> {
    return this.load('District');
  }

  get borough$(): Observable<IReferenceData[]> {
    return this.load('Borough');
  }

  get decisionReasons$(): Observable<IReferenceData[]> {
    return this.load('DecisionReasons');
  }
  get exitReason$(): Observable<IReferenceData[]> {
    return this.load('ExitReason');
  }
  get evaluationType$(): Observable<IReferenceData[]> {
    return this.load('EvaluationType');
  }

  get evaluationStatus$(): Observable<IReferenceData[]> {
    return this.load('EvalStatus');
  }
  get reasonForReSchedule$(): Observable<IReferenceData[]> {
    return this.load('ReasonForReSchedule');
  }

  get placementRecType$(): Observable<IReferenceData[]> {
    return this.load('PlacementRecType');
  }

  get dashboardActivity$(): Observable<IReferenceData[]> {
    return this.load('DashboardActivity');
  }

  get caseStage$(): Observable<IReferenceData[]> {
    return this.load('CaseStage');
  }

  get evaluatorType$(): Observable<IReferenceData[]> {
    return this.load('EvaluatorType');
  }

  get documentType$(): Observable<IReferenceData[]> {
    return this.load('DocumentType');
  }

  get complianceStatus$(): Observable<IReferenceData[]> {
    return this.load('ComplianceStatus');
  }

  get eiChildStatus$(): Observable<IReferenceData[]> {
    return this.load('EIChildStatus');
  }

  get documentStatus$(): Observable<IReferenceData[]> {
    return this.load('DocumentStatus');
  }

  get fosterAgency$(): Observable<IReferenceData[]> {
    return this.load('FosterAgency');
  }

  get eiAutoEligibleCond$(): Observable<IReferenceData[]> {
    return this.load('EIAutoEligibleCond');
  }

  get discipline$(): Observable<IReferenceData[]> {
    return this.load('Discipline');
  }

  get intensity$(): Observable<IReferenceData[]> {
    return this.load('Intensity');
  }

  get groupType$(): Observable<IReferenceData[]> {
    return this.load('GroupType');
  }

  get frequencyInterval$(): Observable<IReferenceData[]> {
    return this.load('FrequencyInterval');
  }

  get oshResponse$(): Observable<IReferenceData[]> {
    return this.load('OSHResponse');
  }

  get nursingReferral$(): Observable<IReferenceData[]> {
    return this.load('NursingReferral');
  }

  get asstTechDeviceType$(): Observable<IReferenceData[]> {
    return this.load('AsstTechDeviceType');
  }

  get asstTechDuration$(): Observable<IReferenceData[]> {
    return this.load('AsstTechDuration');
  }

  get locations$(): Observable<IReferenceData[]> {
    return this.load('Locations');
  }

  get gmfcsLevel$(): Observable<IReferenceData[]> {
    return this.load('GMFCSLevel');
  }

  get rServiceType$(): Observable<IReferenceData[]> {
    return this.load('RServiceType');
  }

  get rEncounterType$(): Observable<IReferenceData[]> {
    return this.load('EncounterType');
  }

  get serviceType$(): Observable<IReferenceData[]> {
    return this.load('ServiceType');
  }
  get ethnicity$(): Observable<IReferenceData[]> {
    return this.load('Ethnicity');
  }

  get iepSection$(): Observable<IReferenceData[]> {
    return this.load('IEPSection');
  }

  get cspOptCons$(): Observable<IReferenceData[]> {
    return this.load('CSPOptCons');
  }

  get assessUsed$(): Observable<IReferenceData[]> {
    return this.load('AssessUsed');
  }

  get compServices$(): Observable<IReferenceData[]> {
    return this.load('CompServices');
  }

  get subject$(): Observable<IReferenceData[]> {
    return this.load('Subject');
  }

  get svcProvidedBy$(): Observable<IReferenceData[]> {
    return this.load('SvcProvidedBy');
  }

  get pServiceType$(): Observable<IReferenceData[]> {
    return this.load('PServiceType');
  }

  get svcLocation$(): Observable<IReferenceData[]> {
    return this.load('SvcLocation');
  }

  get classroomSetting$(): Observable<IReferenceData[]> {
    return this.load('ClassroomSetting');
  }

  get frequencyNumber$(): Observable<IReferenceData[]> {
    return this.load('FrequencyNumber');
  }

  get otherConsid$(): Observable<IReferenceData[]> {
    return this.load('OtherConsid');
  }

  get hoursDuration$(): Observable<IReferenceData[]> {
    return this.load('HoursDuration');
  }

  get suppAidsType$(): Observable<IReferenceData[]> {
    return this.load('SuppAidsType');
  }

  get paraProType$(): Observable<IReferenceData[]> {
    return this.load('ParaProType');
  }

  get healthOptions$(): Observable<IReferenceData[]> {
    return this.load('HealthOptions');
  }

  get intervalDuration$(): Observable<IReferenceData[]> {
    return this.load('IntervalDuration');
  }

  get paraDuration$(): Observable<IReferenceData[]> {
    return this.load('ParaDuration');
  }

  get groupSize$(): Observable<IReferenceData[]> {
    return this.load('GroupSize');
  }

  get ratioOptions$(): Observable<IReferenceData[]> {
    return this.load('RatioOptions');
  }

  get preSchoolRatio$(): Observable<IReferenceData[]> {
    return this.load('PreSchoolRatio');
  }

  get ratio$(): Observable<IReferenceData[]> {
    return this.load('Ratio');
  }

  get deferredPlacements$(): Observable<IReferenceData[]> {
    return this.load('DeferredPlacements');
  }

  get dayType$(): Observable<IReferenceData[]> {
    return this.load('DayType');
  }

  get referralRequestGrades$(): Observable<IReferenceData[]> {
    return this.load('ReferralRequestGrades');
  }

  get testAccommodation$(): Observable<IReferenceData[]> {
    return this.load('TestAccommodation');
  }

  get transpoRecommend$(): Observable<IReferenceData[]> {
    return this.load('TranspoRecommend');
  }

  get ambulatoryCodes$(): Observable<IReferenceData[]> {
    return this.load('AmbulatoryCodes');
  }

  get ineligDetReasons$(): Observable<IReferenceData[]> {
    return this.load('IneligDetReasons');
  }

  get progressReport$(): Observable<IReferenceData[]> {
    return this.load('ProgressReport');
  }

  get measurementMethod$(): Observable<IReferenceData[]> {
    return this.load('MeasurementMethod');
  }

  get progressReasons$(): Observable<IReferenceData[]> {
    return this.load('ProgressReasons');
  }

  get progressStatus$(): Observable<IReferenceData[]> {
    return this.load('ProgressStatus');
  }

  get preSchoolType$(): Observable<IReferenceData[]> {
    return this.load('PreSchoolType');
  }

  get providerType$(): Observable<IReferenceData[]> {
    return of(Object.values(Constants.PROVIDER_TYPES_ROLE_BASED));
    return this.load('ProviderType');
  }

  get schoolYear$(): Observable<IReferenceData[]> {
    return of(Constants.SCHL_YR_PM_DROPDOWN);
    //  return this.load('SchoolYear');
  }

  get schoolYears$(): Observable<IReferenceData[]> {
    return this.load('SchoolYear');
  }
  get specializedProgram$(): Observable<IReferenceData[]> {
    return this.load('SpecializedProgram');
  }
  get monoBilingual$(): Observable<IReferenceData[]> {
    return of(Constants.MONO_BILI_DROPDOWN);
  }

  get bundleStatus$(): Observable<IReferenceData[]> {
    return of(Constants.BUNDLE_STATUS_DROPDOWN);
  }

  get cseDistrictRefValues$(): Observable<IReferenceData[]> {
    return of(Constants.CSE_REF_VALUE);
  }

  get cse$(): Observable<IReferenceData[]> {
    return of(Constants.CSE);
  }

  get pni$(): Observable<IReferenceData[]> {
    return of(Constants.PNI_DRPDWN);
  }

  get mandateStatusTabs$(): Observable<IReferenceData[]> {
    return this.load('Mandates');
  }

  get assessmentStatusTabs$(): Observable<IReferenceData[]> {
    return this.load('Assessments');
  }

  get caseCloseReason$(): Observable<IReferenceData[]> {
    return this.load('CaseCloseReason');
  }

  get interventionType$(): Observable<IReferenceData[]> {
    return this.load('InterventionType');
  }

  get redAlert$(): Observable<IReferenceData[]> {
    return this.load('RedAlert');
  }

  get grade$(): Observable<IReferenceData[]> {
    return this.load('Grade');
  }
  get ages$(): Observable<IReferenceData[]> {
    return of(Constants.AGES);
  }

  get gradeLevel$(): Observable<IReferenceData[]> {
    return this.load('GradeLevel');
  }

  get serviceModels$(): Observable<IReferenceData[]> {
    return of(Constants.SERVICE_MODELS);
  }
  get evaluatorTypeStatus$(): Observable<IReferenceData[]> {
    return of(Constants.EVALUATOR_TYPES_STATUS);
  }
  get assessmentAssignmentStatus$(): Observable<IReferenceData[]> {
    return of(Constants.EA_STATUS);
  }
  get filterServiceType$(): Observable<IReferenceData[]> {
    return of(Constants.SERVICE_TYPES);
  }
  get PAServiceType$(): Observable<IReferenceData[]> {
    return this.load('PAServiceType');
  }

  get caseActionStatus$(): Observable<IReferenceData[]> {
    return this.load('CaseActionStatus');
  }

  get sex$(): Observable<IReferenceData[]> {
    return this.load('Sex');
  }

  get dbn$(): Observable<IReferenceData[]> {
    return this.load('Dbn');
  }

  get extReq$(): Observable<IReferenceData[]> {
    return this.load('ExtReq');
  }

  get assessmentRejection$(): Observable<IReferenceData[]> {
    return this.load('AssessmentRejection');
  }

  get dischargeReason$(): Observable<IReferenceData[]> {
    return this.load('DischargeReason');
  }

  get reasonForDelay$(): Observable<IReferenceData[]> {
    return this.load('ReasonForDelay');
  }

  get engagementstatus$(): Observable<IReferenceData[]> {
    return this.load('Engagementstatus');
  }

  get outreachMethod$(): Observable<IReferenceData[]> {
    return this.load('OutreachMethod');
  }

  get studRelatedEvents$(): Observable<IReferenceData[]> {
    return this.load('StudRelatedEvents');
  }

  get eiOutreachToFrom$(): Observable<IReferenceData[]> {
    return this.load('EIOutreachToFrom');
  }
  get assessmentType$(): Observable<IReferenceData[]> {
    return this.load('AssignmentType');
  }

  get schoolKind$(): Observable<IReferenceData[]> {
    return this.load('SchoolKind');
  }

  get programCode$(): Observable<IReferenceData[]> {
    return this.load('ProgramCode');
  }

  get classType$(): Observable<IReferenceData[]> {
    return this.load('ClassType');
  }

  get overallRatio$(): Observable<IReferenceData[]> {
    return this.load('OveralRatio');
  }

  get seRatio$(): Observable<IReferenceData[]> {
    return this.load('SERatio');
  }

  get studentType$(): Observable<IReferenceData[]> {
    return of(Constants.STUDENT_TYPE);
  }

  get mandateTerm$(): Observable<IReferenceData[]> {
    return of(Constants.MANDATE_TERM);
  }

  get lcgmsLocations$(): Observable<IReferenceData[]> {
    return this.load('lcgmsLocations');
  }

  get varianceStatus$(): Observable<IReferenceData[]> {
    return this.load('VarianceStatus');
  }

  get monthYear$(): Observable<IReferenceData[]> {
    return this.load('MonthYear');
  }

  get sessionType$(): Observable<IReferenceData[]> {
    return this.load('SessionType');
  }

  get scheduleLocationType$(): Observable<IReferenceData[]> {
    return this.load('PAScheduleLocationType');
  }

  get repeatUntil$(): Observable<IReferenceData[]> {
    return this.load('PAMeetingEDV');
  }

  get occurences$(): Observable<IReferenceData[]> {
    return this.load('PAMeetingFrequency');
  }

  get months$(): Observable<IReferenceData[]> {
    return of(Constants.MONTHS);
  }

  get weekDays$(): Observable<IReferenceData[]> {
    return of(Constants.DAYS);
  }

  get weekOrder$(): Observable<IReferenceData[]> {
    return of(Constants.WEEKORDER);
  }

  get reasonForDecline$(): Observable<IReferenceData[]> {
    return this.load('ReasonForDecline');
  }
  get reasonForReject$(): Observable<IReferenceData[]> {
    return this.load('ReasonsForReject');
  }
  get neverServedReasons$(): Observable<IReferenceData[]> {
    return this.load('NeverServedReasons');
  }
  get reasonsForWithdraw$(): Observable<IReferenceData[]> {
    return this.load('ReasonsForWithdraw');
  }
  get reasonForWithdraw$(): Observable<IReferenceData[]> {
    return this.load('ReasonForWithdraw');
  }

  get reasonsForTerminate$(): Observable<IReferenceData[]> {
    return this.load('ReasonsForTerminate');
  }
  get reasonsForReassign$(): Observable<IReferenceData[]> {
    return this.load('ReasonsForReassign');
  }
  get rsaServiceType$(): Observable<IReferenceData[]> {
    return this.load('RsaServiceType');
  }
  get assessmentAuthorizationReason$(): Observable<IReferenceData[]> {
    return this.load('AssessmentAuthorizationReasons');
  }
  get mandateAssignmentReason$(): Observable<IReferenceData[]> {
    return this.load('MandateAssignmentReason  ');
  }
  get reasonForRateDiff$(): Observable<IReferenceData[]> {
    return this.load('ReasonForRateDiff  ');
  }
  get reasonForDisApproval$(): Observable<IReferenceData[]> {
    return this.load('ReasonForDisApproval  ');
  }
  get reasonForReAssign$(): Observable<IReferenceData[]> {
    return this.load('ReasonForReAssign  ');
  }
  get encounterStatus$(): Observable<IReferenceData[]> {
    return this.load('EncounterStatus  ');
  }
  get deskAccessibility$(): Observable<IReferenceData[]> {
    return this.load('DeskAccessibility');
  }
  get communicationBoardType$(): Observable<IReferenceData[]> {
    return this.load('CommunicationBoardType');
  }
  get attentionSpan$(): Observable<IReferenceData[]> {
    return this.load('AttentionSpan');
  }
  get seatingOrPositioning$(): Observable<IReferenceData[]> {
    return this.load('SeatingOrPositioning');
  }
  get referralType$(): Observable<IReferenceData[]> {
    return this.load('ReferralType');
  }
  get communicationType$(): Observable<IReferenceData[]> {
    return this.load('CommunicationType');
  }
  get studentAbility$(): Observable<IReferenceData[]> {
    return this.load('StudentAbility');
  }
  get skillPerformed$(): Observable<IReferenceData[]> {
    return this.load('SkillPerformed');
  }
  get vocabularySize$(): Observable<IReferenceData[]> {
    return this.load('VocabularySize');
  }
  get studentClassroomType$(): Observable<IReferenceData[]> {
    return this.load('StudentClassroomType');
  }
  get brailleLevels$(): Observable<IReferenceData[]> {
    return this.load('BrailleLevels');
  }

  get atSubType$(): Observable<IReferenceData[]> {
    return this.load('ATSubType');
  }
  get CommunicationType$(): Observable<IReferenceData[]> {
    return this.load('CommunicationType');
  }
  get ReferralType$(): Observable<IReferenceData[]> {
    return this.load('ReferralType');
  }
  get StudentAbility$(): Observable<IReferenceData[]> {
    return this.load('StudentAbility');
  }
  get AttentionSpan$(): Observable<IReferenceData[]> {
    return this.load('AttentionSpan');
  }
  get SkillPerformed$(): Observable<IReferenceData[]> {
    return this.load('SkillPerformed');
  }
  get VocabularySize$(): Observable<IReferenceData[]> {
    return this.load('VocabularySize');
  }
  get StudentClassroomType$(): Observable<IReferenceData[]> {
    return this.load('StudentClassroomType');
  }
  get BrailleLevels$(): Observable<IReferenceData[]> {
    return this.load('BrailleLevels');
  }
  get BrailleDevicesUsed$(): Observable<IReferenceData[]> {
    return this.load('BrailleDevicesUsed');
  }
  get VocabularyType$(): Observable<IReferenceData[]> {
    return this.load('VocabularyType');
  }

  get MedTranspoAccom$(): Observable<IReferenceData[]> {
    return this.load('MedTranspoAccom');
  }
  get MedTransPhyApp$(): Observable<IReferenceData[]> {
    return this.load('MedTransPhyApp');
  }
  get assignmentStatus$(): Observable<IReferenceData[]> {
    return of(Constants.ASSIGNMENT_STATUS);
  }
  get mandateStatus$(): Observable<IReferenceData[]> {
    return of(Constants.MANDATE_STATUS);
  }
  get ratio1$(): Observable<IReferenceData[]> {
    return of(Constants.RATIO_NUMBER_1);
  }
  get classRatio1$(): Observable<IReferenceData[]> {
    return of(Constants.RATIO_NUMBER_2);
  }
  get ratio3$(): Observable<IReferenceData[]> {
    return of(Constants.RATIO_NUMBER_3);
  }
  get classRatio2$(): Observable<IReferenceData[]> {
    return of(Constants.RATIO_NUMBER_4);
  }
  get additionalContactType$(): Observable<IReferenceData[]> {
    return this.load('AdditionalContactType');
  }
  get attention$(): Observable<IReferenceData[]> {
    return this.load('Attention');
  }
  get clusterServiceType$(): Observable<IReferenceData[]> {
    return this.load('ClusterServiceType');
  }
  get clusterFamilyWorkers$(): Observable<IReferenceData[]> {
    return this.load('ClusterFamilyWorkers');
  }
  get clusterRetention$(): Observable<IReferenceData[]> {
    return this.load('ClusterRetention');
  }
  get clusterDistrict$(): Observable<IReferenceData[]> {
    return this.load('ClusterDistrict');
  }

  get(keys: string[]): Observable<Record<string, IReferenceData[]>> {
    const sources: Observable<readonly [string, IReferenceData[]]>[] = keys.map(
      (key: string) =>
        this.load(key).pipe(
          map((data: IReferenceData[]) => [key, data] as const)
        )
    );

    return forkJoin(sources).pipe(
      map((entries: (readonly [string, IReferenceData[]])[]) =>
        Object.fromEntries(entries)
      )
    );
  }
}
